import type {
  GetServerSideProps,
  GetServerSidePropsContext,
  GetServerSidePropsResult
} from 'next';
import { ParsedUrlQuery } from 'querystring';
import { FC } from 'react';
import { Category } from 'src/api/types';
import Head from 'next/head';
import { PROD_DOMAIN } from 'src/api/constants';
import getLettersList, {
  GetLettersResponse
} from 'src/api/requests/getLetters';
import { robotsTagContent } from 'src/utils/seo';
import { fetchCache } from 'src/api/caching/cacheUtils';
import { triggerCustomStatusCodeRedirect } from 'src/ssr/errorStatusCodeRedirect';
import {
  GeneralServerProps,
  generalSSRProps,
  getCacheConfig,
  isQueryParamsValid,
  setSSRCacheControl
} from '../ssr/generalSsrUtil';
import HomeContainer from '../page-components/home/HomeContainer';

interface Props extends GeneralServerProps {
  categoriesList: Category[];
  lettersListResponse: GetLettersResponse[];
}

const HomePage: FC<Props> = ({ lettersListResponse }) => {
  return (
    <>
      <Head>
        <meta name="robots" content={robotsTagContent} key="robots" />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: `{
                    "@context": "https://schema.org",
                    "@type": "Organization",
                    "url": "${PROD_DOMAIN}/",
                    "logo": "https://spaces.likie.ua/drug/LOGO_LikiE_XXL.png"
                  }`
          }}
        ></script>
      </Head>
      <HomeContainer lettersListResponse={lettersListResponse} />
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async (
  context: GetServerSidePropsContext<ParsedUrlQuery>
) => {
  setSSRCacheControl(context);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return fetchCache<GetServerSidePropsResult<{ [key: string]: any }>>(
    getCacheConfig(context),
    async () => {
      const {
        req: { cookies }
      } = context;
      const generalProps = await generalSSRProps(context);
      if (!isQueryParamsValid(context.query)) {
        // Redirect to 410 because the URL contains invalid query params
        return triggerCustomStatusCodeRedirect(context.res, 410);
      }
      const attr = {
        cookies,
        ip: generalProps.ip,
        locale: context?.locale,
        useCache: true
      };
      const lettersListResponse = await getLettersList(attr);
      return {
        props: {
          ...generalProps,
          lettersListResponse
        }
      };
    }
  );
};

export default HomePage;
